import { Icon } from '@clarion/ClarionIcon';
import { ReactNode } from 'react';
import {
  Checkbox as AriaCheckbox,
  CheckboxGroup as AriaCheckboxGroup,
  CheckboxGroupProps as AriaCheckboxGroupProps,
  CheckboxProps,
  ValidationResult,
  composeRenderProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { ClarionDescription, ClarionFieldError, ClarionLabel } from './ClarionField';
import { composeTailwindRenderProps, focusRing } from './utils';

export interface CheckboxGroupProps extends Omit<AriaCheckboxGroupProps, 'children'> {
  label?: string;
  children?: ReactNode;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
}

export function ClarionCheckboxGroup(props: CheckboxGroupProps) {
  return (
    <AriaCheckboxGroup
      {...props}
      className={composeTailwindRenderProps(props.className, 'flex flex-col gap-2')}
    >
      <ClarionLabel>{props.label}</ClarionLabel>
      {props.children}
      {props.description && <ClarionDescription>{props.description}</ClarionDescription>}
      <ClarionFieldError>{props.errorMessage}</ClarionFieldError>
    </AriaCheckboxGroup>
  );
}

const checkboxStyles = tv({
  base: 'flex gap-2 group text-sm transition',
  variants: {
    isDisabled: {
      false: 'text-gray-800 dark:text-gray-200',
      true: 'text-gray-300 dark:text-gray-600 forced-colors:text-[GrayText]',
    },
  },
});

const boxStyles = tv({
  extend: focusRing,
  base: 'w-5 h-5 shrink-0 rounded-sm flex items-center justify-center border transition',
  variants: {
    isSelected: {
      false:
        'bg-white dark:bg-gray-900 border-(--color) [--color:var(--color-gray-400)] dark:[--color:colors.gray-400)] group-pressed:[--color:var(--color-gray-500)] dark:group-pressed:[--color:var(--color-gray-300)] group-hover:[--color:var(--color-gray-500)] dark:group-hover:[--color:var(--color-gray-300)] group-pressed:border-2 group-hover:bg-gray-50 dark:group-hover:bg-gray-800',
      true: 'bg-(--color) border-(--color) [--color:var(--color-purple-600)] group-pressed:[--color:var(--color-purple-700)] dark:[--color:var(--color-purple-500)] dark:group-pressed:[--color:var(--color-purple-400)] group-hover:[--color:var(--color-purple-700)] dark:group-hover:[--color:var(--color-purple-400)] group-pressed:border-2 group-hover:bg-opacity-90 dark:group-hover:bg-opacity-90 forced-colors:[--color:Highlight]!',
    },
    isInvalid: {
      true: '[--color:var(--color-red-700)] dark:[--color:var(--color-red-600)] forced-colors:[--color:Mark]! group-pressed:[--color:var(--color-red-800)] dark:group-pressed:[--color:var(--color-red-700)] group-hover:[--color:var(--color-red-800)] dark:group-hover:[--color:var(--color-red-700)] group-pressed:border-2 group-hover:bg-opacity-90 dark:group-hover:bg-opacity-90',
    },
    isDisabled: {
      true: '[--color:var(--color-gray-200)] dark:[--color:var(--color-gray-700)] forced-colors:[--color:GrayText]!',
    },
  },
});

const iconStyles =
  'w-4 h-4 text-white group-disabled:text-gray-400 dark:text-slate-900 dark:group-disabled:text-slate-600 forced-colors:text-[HighlightText]';

export function ClarionCheckbox({ children, ...props }: CheckboxProps) {
  return (
    <AriaCheckbox
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        checkboxStyles({ ...renderProps, className }),
      )}
    >
      {composeRenderProps(children, (children, { isSelected, isIndeterminate, ...renderProps }) => (
        <>
          <div className={boxStyles({ isSelected: isSelected || isIndeterminate, ...renderProps })}>
            {isIndeterminate ? (
              <Icon
                icon="minus"
                className={iconStyles}
                size={16}
                lucideProps={{ strokeWidth: 3 }}
              />
            ) : isSelected ? (
              <Icon icon="tick" className={iconStyles} size={16} lucideProps={{ strokeWidth: 3 }} />
            ) : null}
          </div>
          {children}
        </>
      ))}
    </AriaCheckbox>
  );
}
